import React, { useCallback, useMemo, useRef, useState } from 'react';
import { lookupApi } from './lookupApi';
import './lookup.css';

// Mirrors the regex registry in services/cms_msdrg_ingester.py so the UI can
// flag known vs unknown files before the user clicks "Run Ingestion".
const KNOWN_PATTERNS = [
  { rx: /^appendix[_-]?a\.txt$/i, label: 'MS-DRG list (Appendix A)' },
  { rx: /^appendix[_-]?b\.txt$/i, label: 'ICD-10-CM Index (Appendix B)' },
  { rx: /^appendix[_-]?c\.txt$/i, label: 'CC/MCC + PDX (Appendix C)' },
  { rx: /^appendix[_-]?d[_-]?e\.txt$/i, label: 'Surgical Hierarchy + PCS (D/E)' },
  { rx: /^appendix[_-]?f[_-]?i\.txt$/i, label: 'Unrelated O.R. Procedures (F-I)' },
  { rx: /^mdcs?_\d+_\d+\.txt$/i, label: 'MDC chapter file' },
  { rx: /^definitions[_\s-]?of[_\s-]?medicare[_\s-]?code[_\s-]?edits.*\.txt$/i, label: 'Medicare Code Edits manual' },
];

function detectType(filename) {
  const base = (filename || '').toLowerCase();
  for (const entry of KNOWN_PATTERNS) {
    if (entry.rx.test(base)) return entry.label;
  }
  return null;
}

function fmtBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtInt(n) {
  return Number.isFinite(n) ? n.toLocaleString() : '0';
}

/**
 * CMS MS-DRG Definitions Manual pack ingestion. Account-global scope only.
 * Props: onIngested (callback after success).
 */
export default function LookupCmsPackUpload({ onIngested }) {
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [defaults, setDefaults] = useState({
    source: 'CMS MS-DRG Definitions Manual',
    version: 'v43.1',
    effectiveDate: '',
  });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const addFiles = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setError(null);
    setResult(null);
    setFiles((prev) => {
      const byName = new Map(prev.map((f) => [f.name, f]));
      for (const f of incoming) byName.set(f.name, f);
      return Array.from(byName.values());
    });
  }, []);

  const handleFileInputChange = useCallback((e) => {
    addFiles(Array.from(e.target.files || []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addFiles]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  }, [addFiles]);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setDragActive(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setDragActive(false); }, []);
  const removeFile = useCallback((name) => setFiles((prev) => prev.filter((f) => f.name !== name)), []);

  const annotatedFiles = useMemo(
    () => files.map((f) => ({ file: f, name: f.name, size: f.size, detected: detectType(f.name) })),
    [files],
  );
  const knownCount = useMemo(() => annotatedFiles.filter((f) => f.detected).length, [annotatedFiles]);

  const handleRun = useCallback(async () => {
    if (files.length === 0) { setError('Pick at least one file before running ingestion.'); return; }
    if (knownCount === 0) { setError('None of the selected files match a known CMS parser. Check the filenames.'); return; }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const opts = {
        source: defaults.source.trim() || 'CMS MS-DRG Definitions Manual',
        version: defaults.version.trim() || 'v43.1',
        effectiveDate: defaults.effectiveDate || null,
      };
      const resp = await lookupApi.ingestCmsPack(files, opts);
      if (!resp || resp.success === false) throw new Error(resp?.message || 'Ingestion failed');
      setResult(resp.data);
      if (onIngested) onIngested(resp.data);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Ingestion failed');
    } finally {
      setRunning(false);
    }
  }, [files, knownCount, defaults, onIngested]);

  return (
    <div className="cms-pack">
      <section className="cms-pack__section">
        <h2>1. Choose files</h2>
        <div
          className={`cms-pack-dropzone ${dragActive ? 'cms-pack-dropzone--active' : ''}`}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <strong>Click to choose</strong> or drag-and-drop multiple .txt files here.
          <input ref={fileInputRef} type="file" accept=".txt" multiple style={{ display: 'none' }} onChange={handleFileInputChange} />
        </div>
        <div className="cms-pack-known-list">
          Known patterns: {KNOWN_PATTERNS.map((p) => (
            <code key={p.label}>{p.rx.toString().replace(/^\/|\/i$/g, '')}</code>
          ))}
        </div>

        {annotatedFiles.length > 0 && (
          <div className="cms-pack-files">
            <table>
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Size</th>
                  <th>Detected Type</th>
                  <th style={{ width: 80 }} aria-label="actions"></th>
                </tr>
              </thead>
              <tbody>
                {annotatedFiles.map((row) => (
                  <tr key={row.name}>
                    <td>{row.name}</td>
                    <td>{fmtBytes(row.size)}</td>
                    <td>
                      {row.detected ? (
                        <span className="cms-pack-chip cms-pack-chip--known">{row.detected}</span>
                      ) : (
                        <span className="cms-pack-chip cms-pack-chip--unknown">Unknown - skipped</span>
                      )}
                    </td>
                    <td>
                      <button type="button" className="cms-pack-btn cms-pack-btn--ghost" onClick={() => removeFile(row.name)} disabled={running} style={{ padding: '4px 10px', fontSize: 12 }}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="cms-pack__section">
        <h2>2. Defaults applied to every record</h2>
        <div className="cms-pack-options">
          <div>
            <label htmlFor="cms-pack-source">Source</label>
            <input id="cms-pack-source" type="text" value={defaults.source} onChange={(e) => setDefaults({ ...defaults, source: e.target.value })} disabled={running} />
          </div>
          <div>
            <label htmlFor="cms-pack-version">Version</label>
            <input id="cms-pack-version" type="text" value={defaults.version} onChange={(e) => setDefaults({ ...defaults, version: e.target.value })} disabled={running} />
          </div>
          <div>
            <label htmlFor="cms-pack-effective">Effective Date</label>
            <input id="cms-pack-effective" type="date" value={defaults.effectiveDate} onChange={(e) => setDefaults({ ...defaults, effectiveDate: e.target.value })} disabled={running} />
          </div>
        </div>
      </section>

      <section className="cms-pack__section">
        <h2>3. Run ingestion</h2>
        {error && <div className="cms-pack-error">{error}</div>}

        <div className="cms-pack-actions">
          <button type="button" className="cms-pack-btn cms-pack-btn--primary" disabled={running || files.length === 0} onClick={handleRun}>
            {running && <span className="cms-pack-spinner" />}
            {running ? 'Ingesting...' : `Run Ingestion (${knownCount} known / ${files.length} files)`}
          </button>
        </div>

        {result && (
          <div style={{ marginTop: 16 }}>
            <div className="cms-pack-totals">
              <div className="cms-pack-stat">
                <span className="cms-pack-stat__num">{fmtInt(result.totalParsed)}</span>
                <span className="cms-pack-stat__label">Total parsed</span>
              </div>
              <div className="cms-pack-stat">
                <span className="cms-pack-stat__num">{fmtInt(result.totalInserted)}</span>
                <span className="cms-pack-stat__label">Inserted</span>
              </div>
              <div className="cms-pack-stat">
                <span className="cms-pack-stat__num">{fmtInt(result.totalSkipped)}</span>
                <span className="cms-pack-stat__label">Duplicates skipped</span>
              </div>
            </div>

            <div className="cms-pack-files">
              <table>
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Detected Type</th>
                    <th style={{ textAlign: 'right' }}>Parsed</th>
                    <th style={{ textAlign: 'right' }}>Inserted</th>
                    <th style={{ textAlign: 'right' }}>Duplicates</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.files || []).map((row) => (
                    <tr key={row.filename}>
                      <td>{row.filename}</td>
                      <td>
                        {row.detectedType ? (
                          <span className="cms-pack-chip cms-pack-chip--known">{row.detectedType}</span>
                        ) : (
                          <span className="cms-pack-chip cms-pack-chip--unknown">unknown</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtInt(row.parsed)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtInt(row.inserted)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtInt(row.skippedDuplicates)}</td>
                      <td style={{ color: row.error ? '#991b1b' : '#6b7280' }}>{row.error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {result.unknownFiles && result.unknownFiles.length > 0 && (
              <div className="cms-pack-known-list" style={{ marginTop: 10 }}>
                <strong>Skipped (unknown filename):</strong> {result.unknownFiles.join(', ')}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
